import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AccessTokenService } from 'src/app/Services/access-token.service';
import { AlertService } from 'src/app/Services/alert.service';
import { DynamicDataService } from 'src/app/Services/dynamic-data.service';
import { UserDataProviderService } from 'src/app/Services/user-data-provider.service';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/Shared/MaterialModule/material/material.module';
import { computeInitials } from 'src/app/Shared/utils/user-initials';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { RbacService } from 'src/app/Services/rbac.service';
import { Role } from 'src/app/Store/rbac-sod';
import { escalatesTo } from 'src/app/Components/admin-console/workflow-config/escalation-rank.util';

class ImageSnippet {
  pending = false;
  status = 'init';

  constructor(public src: string, public file: File) { }
}

export class UserRole {
  constructor(public prName: string) {
  }
}

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [CommonModule, MaterialModule, ReactiveFormsModule, FormsModule, TranslateModule, ImageCropperComponent],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UserProfileComponent implements OnInit {
  selectedFile!: ImageSnippet;
  userFormGroup!: FormGroup;
  allProfiles = [
    new UserRole('Admin'),
    new UserRole('Member')
  ]

  @ViewChild('imageInput', { static: true }) imageInput!: ElementRef;

  imageChangedEvent: any = '';
  croppedImage: any;
  userData: any;
  validFileUploaded = false;
  pictureRemoved = false;
  userName = '';
  userProfile: any;

  pageDynamicData: any[] = [];

  // Read-only RBAC hierarchy, rendered as capsules: the user's role(s), the rank of their
  // most-senior role, and who approvals escalate to (derived from RBAC, never saved).
  hierarchyRoles: string[] = [];
  hierarchyRank: number | null = null;
  hierarchyEscalatesTo: string | null = null;
  hierarchyLoading = true;

  constructor(public dialogRef: MatDialogRef<UserProfileComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private _userDataProvider: UserDataProviderService,
    private alertService: AlertService,
    private accessTokenService: AccessTokenService,
    public dynamicDataService: DynamicDataService,
    private translate: TranslateService,
    private rbacService: RbacService,
  ) {

    this.userFormGroup = this.formBuilder.group({
      userName: [this.accessTokenService.getUserName(), [Validators.required]],
      userDesignation: [this.accessTokenService.getUserDesignation(), [Validators.required]],
      userEmail: [{ value: this.accessTokenService.getUserEmail(), disabled: true }, [Validators.required, Validators.email]],
      userRole: [this.accessTokenService.getUserRole()]
    });
    this.userName = this.accessTokenService.getUserName();
  }

  ngOnInit(): void {
    this.loadUserData();
    this.loadHierarchy();
    if (this.dynamicDataService.getDataFromLocal('MyProfile') != null) {
      this.pageDynamicData = this.dynamicDataService.getDataFromLocal('MyProfile');
    } else {
      this.getPageData();
    }
  }

  /**
   * Builds the read-only "Hierarchy" line from the RBAC module: the caller's role(s), the
   * rank of their most-senior role, and the role approvals escalate to. `getMePermissions()`
   * returns the user's roles without rank, so it is joined against the full role catalogue
   * (`getRoles()`, which carries `rank`); the escalate-to target reuses the same seniority
   * rule the workflow escalation ladder uses (escalation-rank.util). Read-only — not saved.
   */
  private loadHierarchy(): void {
    this.hierarchyLoading = true;
    forkJoin({
      me: this.rbacService.getMePermissions(),
      roles: this.rbacService.getRoles(),
    }).subscribe({
      next: ({ me, roles }) => {
        const myRoleIds = new Set((me?.roles ?? []).map((r: any) => r.roleId));
        const myRoles: Role[] = roles.filter(r => myRoleIds.has(r.roleId));
        this.hierarchyRoles = (myRoles.length ? myRoles.map(r => r.name) : (me?.roles ?? []).map((r: any) => r.name))
          .filter(Boolean);

        // Highest-ranked role drives the escalation chain (unranked roles sort last).
        const top = myRoles
          .filter(r => r.rank != null)
          .sort((a, b) => (b.rank as number) - (a.rank as number))[0];
        this.hierarchyRank = top ? (top.rank as number) : null;
        this.hierarchyEscalatesTo = top ? (escalatesTo(top.roleId, roles)?.name ?? null) : null;
        this.hierarchyLoading = false;
      },
      error: () => {
        this.hierarchyRoles = [];
        this.hierarchyRank = null;
        this.hierarchyEscalatesTo = null;
        this.hierarchyLoading = false;
      },
    });
  }

  getPageData() {
    const requestBody = {
      UserID: this.accessTokenService.getUserId(),
      pageName: 'MyProfile'
    };
    this.dynamicDataService.getPageData(requestBody).subscribe((res: any) => {
      if (res['status'] == 'success') {
        this.pageDynamicData = res['data'];
        this.dynamicDataService.setDataToLocal('MyProfile', this.pageDynamicData);
      } else {
        this.alertService.openAlert(res['message'], 'error');
      }
    });
  }

  loadUserData() {
    
    const req = {
      Organization_Id: this.accessTokenService.getOrganizationId(),
      UserId: this.accessTokenService.getUserId().toString()
    }
    this._userDataProvider.getUser(req).subscribe((response: any) => {
      
      if (response.status == 'success') {
        this.userData = response.data[0]
        this.userProfile = this.userData.UserProfilePic;
        if (!this.validFileUploaded && !this.pictureRemoved) {
          this.croppedImage = this.userProfile;
        }
        this.userFormGroup.updateValueAndValidity();
        this.userFormGroup.controls['reloaded'];
      } else {
      }
    });
  }

  imageInputClick() {
    setTimeout(() => {
         document.getElementById('imageInput')?.click();
    });
  }

  removePicture() {
    this.userProfile = '';
    this.pictureRemoved = true;
    localStorage.removeItem('profilePicture');
    this.croppedImage = ''
  }

  fileChangeEvent(event: any): void {
    this.imageChangedEvent = event;
    if (event.target.files.length > 0) {
      this.validFileUploaded = true;
    } else {
      this.validFileUploaded = false;
    }
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }

  submitted = false;
  saveProfile() {
    
    this.submitted = true;
    
    if (this.userFormGroup.invalid) return;

    this.croppedImage = this.croppedImage == undefined ? '' : this.croppedImage.toString();

    const requestBody = {
      Organization_Id: this.accessTokenService.getOrganizationId(),
      UserId: this.accessTokenService.getUserId().toString(),
      UserName: this.userFormGroup.controls['userName'].value,
      UserEmail: this.userFormGroup.controls['userEmail'].value,
      UserRole: this.userFormGroup.controls['userRole'].value,
      UserDesignation: this.userFormGroup.controls['userDesignation'].value,
      UserProfilePic: this.croppedImage,
      Status: 'Active',
      ActionBy: this.accessTokenService.getUserId().toString(),
    }
    this._userDataProvider.updateUserData(requestBody).subscribe((response: any) => {
      if (response.status == 'success') {
        this.setLocal();
        this.alertService.openAlert(this.translate.instant('Record saved successfully !'), 'success');
        this.dialogRef.close({ refreshPage: true });
      } else {
        this.alertService.openAlert(this.translate.instant('Something went wrong, try again later !'), 'error');
      }
    });
  }
  get userFormControls() { return this.userFormGroup.controls; }

  get userInitials(): string {
    return computeInitials(this.userName);
  }


  setLocal() {
    const newName        = this.userFormGroup.controls['userName'].value as string;
    const newDesignation = this.userFormGroup.controls['userDesignation'].value as string;

    localStorage.setItem('sk_userName_override',        newName);
    localStorage.setItem('sk_userDesignation_override', newDesignation);
    this.userName = newName;

    // Only overwrite when a new image was actually cropped — picture removal is
    // already handled by removePicture() via localStorage.removeItem
    if (this.croppedImage) {
      const newPic = this.croppedImage.toString();
      localStorage.setItem('profilePicture', newPic);
      this.userProfile = newPic;
    }
  }
}

